import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

import { AppShell } from 'components/dashboard/app-shell';
import { useUser } from 'contexts/UserContext';
import { useToast } from '../components/ui/use-toast';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

import {
  bindTelegramBot,
  getMyTelegramBot,
  unbindTelegramBot,
  type TelegramBotStatus,
} from '../services/notes';

function pickErrorMessage(err: any, fallback: string): string {
  const data = err?.response?.data;
  if (typeof data === 'string') return data;
  if (data?.detail) return String(data.detail);
  if (data?.token && Array.isArray(data.token)) return String(data.token[0]);
  if (data?.token) return String(data.token);
  if (err?.message) return String(err.message);
  return fallback;
}

export default function TelegramSettingsPage() {
  const { user, loading: userLoading } = useUser();
  const { toast } = useToast();

  const [status, setStatus] = useState<TelegramBotStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [token, setToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [unbinding, setUnbinding] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getMyTelegramBot();
      setStatus(res.data ?? { bound: false });
    } catch (err: any) {
      // 404 = nessun bot configurato → trattiamolo come "non bound"
      if (err?.response?.status === 404) {
        setStatus({ bound: false });
      } else {
        setError(pickErrorMessage(err, 'Impossibile caricare lo stato del bot.'));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleBind = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = token.trim();
      if (!trimmed) {
        toast({
          title: 'Token mancante',
          description: 'Incolla il token che ti ha dato BotFather.',
          variant: 'destructive',
        });
        return;
      }
      setSubmitting(true);
      try {
        await bindTelegramBot(trimmed);
        setToken('');
        setShowToken(false);
        toast({
          title: 'Bot collegato',
          description: 'Apri Telegram e manda /start al tuo bot.',
        });
        await fetchStatus();
      } catch (err: any) {
        toast({
          title: 'Collegamento fallito',
          description: pickErrorMessage(err, 'Token non valido o errore del server.'),
          variant: 'destructive',
        });
      } finally {
        setSubmitting(false);
      }
    },
    [token, toast, fetchStatus],
  );

  const handleUnbind = useCallback(async () => {
    setUnbinding(true);
    try {
      await unbindTelegramBot();
      toast({
        title: 'Bot scollegato',
        description: 'Il binding è stato rimosso.',
      });
      await fetchStatus();
    } catch (err: any) {
      toast({
        title: 'Errore',
        description: pickErrorMessage(err, 'Impossibile scollegare il bot.'),
        variant: 'destructive',
      });
    } finally {
      setUnbinding(false);
    }
  }, [toast, fetchStatus]);

  if (userLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Caricamento...</div>
      </div>
    );
  }

  if (!user && !localStorage.getItem('accessToken')) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AppShell currentUser={user}>
      <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6 lg:px-8">
        <header className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight">Telegram bot</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Collega un bot Telegram personale per dettare note e meeting da
            mobile. I messaggi vocali e di testo vengono trasformati in note
            markdown nel tuo CRM.
          </p>
        </header>

        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-6 w-1/3" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-10 w-40" />
          </div>
        ) : error ? (
          <Alert variant="destructive">
            <AlertTitle>Errore</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : status?.bound ? (
          <BoundView
            status={status}
            onUnbind={handleUnbind}
            unbinding={unbinding}
          />
        ) : (
          <UnboundView
            token={token}
            setToken={setToken}
            showToken={showToken}
            setShowToken={setShowToken}
            submitting={submitting}
            onSubmit={handleBind}
          />
        )}
      </div>
    </AppShell>
  );
}

// ---------------------------------------------------------------------------
// Sub-views
// ---------------------------------------------------------------------------

interface UnboundProps {
  token: string;
  setToken: (v: string) => void;
  showToken: boolean;
  setShowToken: (v: boolean) => void;
  submitting: boolean;
  onSubmit: (e: React.FormEvent) => void;
}

function UnboundView({
  token,
  setToken,
  showToken,
  setShowToken,
  submitting,
  onSubmit,
}: UnboundProps) {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Crea il tuo bot con BotFather</CardTitle>
          <CardDescription>
            Segui questi passaggi su Telegram per ottenere un token. Ti basta
            farlo una volta.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal space-y-2 pl-5 text-sm">
            <li>
              Apri Telegram, cerca <strong>@BotFather</strong> e avvia la chat.
            </li>
            <li>
              Invia il comando <code className="bg-muted rounded px-1 py-0.5">/newbot</code>
              {' '}e segui le istruzioni: scegli un <em>nome</em> e uno
              {' '}<em>username</em> (deve finire con <code className="bg-muted rounded px-1 py-0.5">bot</code>).
            </li>
            <li>
              BotFather risponderà con un token nel formato
              {' '}<code className="bg-muted rounded px-1 py-0.5">123456789:ABCdef...</code>.
            </li>
            <li>Incolla il token qui sotto e clicca <strong>Collega bot</strong>.</li>
          </ol>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Collega il bot</CardTitle>
          <CardDescription>
            Il token resta sul tuo backend e non viene mai mostrato di nuovo.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-3">
            <div className="flex gap-2">
              <Input
                type={showToken ? 'text' : 'password'}
                placeholder="123456789:ABCdef..."
                value={token}
                onChange={(e) => setToken(e.target.value)}
                disabled={submitting}
                autoComplete="off"
                spellCheck={false}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setShowToken(!showToken)}
                disabled={submitting}
                aria-label={showToken ? 'Nascondi token' : 'Mostra token'}
              >
                {showToken ? <EyeOff /> : <Eye />}
              </Button>
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={submitting || !token.trim()}>
                {submitting && <Loader2 className="animate-spin" />}
                Collega bot
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

interface BoundProps {
  status: TelegramBotStatus;
  onUnbind: () => void;
  unbinding: boolean;
}

function BoundView({ status, onUnbind, unbinding }: BoundProps) {
  const username = status.bot_username ? `@${status.bot_username}` : 'bot collegato';
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span>{username}</span>
            {status.claimed ? (
              <Badge variant="default">Attivo</Badge>
            ) : (
              <Badge variant="secondary">In attesa di /start</Badge>
            )}
          </CardTitle>
          <CardDescription>
            {status.claimed
              ? 'Il bot è collegato e pronto. Vai su Telegram e manda /nuova per iniziare una nota.'
              : 'Apri Telegram, manda /start al bot per attivarlo, poi /nuova per iniziare una nota.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertTitle>Comandi disponibili</AlertTitle>
            <AlertDescription>
              <ul className="list-disc pl-5 text-sm">
                <li><code className="bg-muted rounded px-1 py-0.5">/start</code> — abilita il bot per il tuo account</li>
                <li><code className="bg-muted rounded px-1 py-0.5">/nuova</code> — inizia una nuova nota o meeting</li>
              </ul>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Scollegare il bot</CardTitle>
          <CardDescription>
            Rimuove il binding fra il bot e il tuo account. Le note già create
            restano accessibili.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" disabled={unbinding}>
                {unbinding && <Loader2 className="animate-spin" />}
                Scollega
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Scollegare il bot?</AlertDialogTitle>
                <AlertDialogDescription>
                  Dopo lo scollegamento dovrai ripetere la procedura con
                  BotFather per ricollegarne uno nuovo. Le note esistenti non
                  vengono cancellate.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annulla</AlertDialogCancel>
                <AlertDialogAction
                  onClick={onUnbind}
                  className="bg-destructive text-white hover:bg-destructive/90"
                >
                  Scollega
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  );
}
